import type { EmphasisAnimation } from "../graphics/types";

/**
 * Pure: it reads an animation description and a time and returns the transform
 * to draw with. It sat in VideoEngine only because that is where the one caller
 * lives.
 */
export function applyEmphasisAnimation(
  animation: EmphasisAnimation,
  time: number,
): {
  opacity: number;
  scale: number;
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
  rotation: number;
} {
  const { type, speed, intensity, loop, startTime, animationDuration } =
    animation;

  const animStart = startTime ?? 0;
  if (time < animStart) {
    return {
      opacity: 1,
      scale: 1,
      scaleX: 1,
      scaleY: 1,
      offsetX: 0,
      offsetY: 0,
      rotation: 0,
    };
  }

  if (animationDuration !== undefined && animationDuration > 0) {
    const animEnd = animStart + animationDuration;
    if (time > animEnd) {
      return {
        opacity: 1,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: 0,
        rotation: 0,
      };
    }
  }

  const adjustedTime = time - animStart;
  const cycleTime = loop
    ? (adjustedTime * speed) % 1
    : Math.min(adjustedTime * speed, 1);
  const t = cycleTime * Math.PI * 2;

  switch (type) {
    case "pulse": {
      const pulseScale = 1 + Math.sin(t) * 0.1 * intensity;
      return {
        opacity: 1,
        scale: pulseScale,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: 0,
        rotation: 0,
      };
    }
    case "shake": {
      const shakeX = Math.sin(t * 5) * 0.02 * intensity;
      const shakeY = Math.cos(t * 5) * 0.02 * intensity;
      return {
        opacity: 1,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: shakeX,
        offsetY: shakeY,
        rotation: 0,
      };
    }
    case "bounce": {
      const bounceY = Math.abs(Math.sin(t)) * -0.05 * intensity;
      return {
        opacity: 1,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: bounceY,
        rotation: 0,
      };
    }
    case "float": {
      const floatY = Math.sin(t) * 0.03 * intensity;
      return {
        opacity: 1,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: floatY,
        rotation: 0,
      };
    }
    case "spin": {
      const spinRotation = cycleTime * 360 * intensity;
      return {
        opacity: 1,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: 0,
        rotation: spinRotation,
      };
    }
    case "flash": {
      const flashOpacity = 0.5 + Math.abs(Math.sin(t)) * 0.5;
      return {
        opacity: flashOpacity,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: 0,
        rotation: 0,
      };
    }
    case "heartbeat": {
      const phase = cycleTime * 4;
      let heartScale = 1;
      if (phase < 1)
        heartScale = 1 + 0.15 * intensity * Math.sin(phase * Math.PI);
      else if (phase < 2)
        heartScale = 1 + 0.1 * intensity * Math.sin((phase - 1) * Math.PI);
      return {
        opacity: 1,
        scale: heartScale,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: 0,
        rotation: 0,
      };
    }
    case "swing": {
      const swingRotation = Math.sin(t) * 15 * intensity;
      return {
        opacity: 1,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: 0,
        rotation: swingRotation,
      };
    }
    case "wobble": {
      const wobbleRotation = Math.sin(t * 3) * 5 * intensity;
      const wobbleX = Math.sin(t) * 0.02 * intensity;
      return {
        opacity: 1,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: wobbleX,
        offsetY: 0,
        rotation: wobbleRotation,
      };
    }
    case "jello": {
      const jelloScaleX = 1 + Math.sin(t * 2) * 0.1 * intensity;
      const jelloScaleY = 1 - Math.sin(t * 2) * 0.1 * intensity;
      return {
        opacity: 1,
        scale: 1,
        scaleX: jelloScaleX,
        scaleY: jelloScaleY,
        offsetX: 0,
        offsetY: 0,
        rotation: 0,
      };
    }
    case "rubber-band": {
      const rubberScaleX = 1 + Math.sin(t) * 0.2 * intensity;
      const rubberScaleY = 1 - Math.sin(t) * 0.1 * intensity;
      return {
        opacity: 1,
        scale: 1,
        scaleX: rubberScaleX,
        scaleY: rubberScaleY,
        offsetX: 0,
        offsetY: 0,
        rotation: 0,
      };
    }
    case "tada": {
      const tadaRotation = Math.sin(t * 4) * 10 * intensity;
      const tadaScale = 1 + Math.sin(t * 2) * 0.1 * intensity;
      return {
        opacity: 1,
        scale: tadaScale,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: 0,
        rotation: tadaRotation,
      };
    }
    case "vibrate": {
      const vibrateX = (Math.random() - 0.5) * 0.02 * intensity;
      const vibrateY = (Math.random() - 0.5) * 0.02 * intensity;
      return {
        opacity: 1,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: vibrateX,
        offsetY: vibrateY,
        rotation: 0,
      };
    }
    case "flicker": {
      const flickerOpacity = Math.random() > 0.1 ? 1 : 0.3;
      return {
        opacity: flickerOpacity,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: 0,
        rotation: 0,
      };
    }
    case "glow": {
      const glowScale = 1 + Math.sin(t) * 0.05 * intensity;
      const glowOpacity = 0.8 + Math.sin(t) * 0.2;
      return {
        opacity: glowOpacity,
        scale: glowScale,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: 0,
        rotation: 0,
      };
    }
    case "breathe": {
      const breatheScale = 1 + Math.sin(t * 0.5) * 0.08 * intensity;
      return {
        opacity: 1,
        scale: breatheScale,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: 0,
        rotation: 0,
      };
    }
    case "wave": {
      const waveY = Math.sin(t + adjustedTime * 2) * 0.03 * intensity;
      const waveRotation = Math.sin(t) * 5 * intensity;
      return {
        opacity: 1,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: waveY,
        rotation: waveRotation,
      };
    }
    case "tilt": {
      const tiltRotation = Math.sin(t * 0.5) * 10 * intensity;
      return {
        opacity: 1,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: 0,
        rotation: tiltRotation,
      };
    }
    case "zoom-pulse": {
      const zoomScale = 1 + Math.sin(t) * 0.15 * intensity;
      return {
        opacity: 1,
        scale: zoomScale,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: 0,
        rotation: 0,
      };
    }
    case "focus-zoom": {
      const focusPoint = animation.focusPoint || { x: 0.5, y: 0.5 };
      const zoomAmount = animation.zoomScale || 1.5;
      const holdDuration = animation.holdDuration || 0.3;
      const zoomInPhase = 0.3;
      const zoomOutPhase = 1 - holdDuration - zoomInPhase;

      let focusScale = 1;
      let focusOffsetX = 0;
      let focusOffsetY = 0;

      if (cycleTime < zoomInPhase) {
        const zoomProgress = cycleTime / zoomInPhase;
        const eased = 1 - Math.pow(1 - zoomProgress, 3);
        focusScale = 1 + (zoomAmount - 1) * eased * intensity;
        focusOffsetX = (0.5 - focusPoint.x) * (focusScale - 1);
        focusOffsetY = (0.5 - focusPoint.y) * (focusScale - 1);
      } else if (cycleTime < zoomInPhase + holdDuration) {
        focusScale = zoomAmount * intensity;
        focusOffsetX = (0.5 - focusPoint.x) * (focusScale - 1);
        focusOffsetY = (0.5 - focusPoint.y) * (focusScale - 1);
      } else {
        const zoomOutProgress =
          (cycleTime - zoomInPhase - holdDuration) / zoomOutPhase;
        const eased = Math.pow(zoomOutProgress, 3);
        focusScale = zoomAmount - (zoomAmount - 1) * eased * intensity;
        focusOffsetX = (0.5 - focusPoint.x) * (focusScale - 1);
        focusOffsetY = (0.5 - focusPoint.y) * (focusScale - 1);
      }

      return {
        opacity: 1,
        scale: focusScale,
        scaleX: 1,
        scaleY: 1,
        offsetX: focusOffsetX,
        offsetY: focusOffsetY,
        rotation: 0,
      };
    }
    case "pan-left": {
      const panLeftX = -cycleTime * 0.2 * intensity;
      return {
        opacity: 1,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: panLeftX,
        offsetY: 0,
        rotation: 0,
      };
    }
    case "pan-right": {
      const panRightX = cycleTime * 0.2 * intensity;
      return {
        opacity: 1,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: panRightX,
        offsetY: 0,
        rotation: 0,
      };
    }
    case "pan-up": {
      const panUpY = -cycleTime * 0.2 * intensity;
      return {
        opacity: 1,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: panUpY,
        rotation: 0,
      };
    }
    case "pan-down": {
      const panDownY = cycleTime * 0.2 * intensity;
      return {
        opacity: 1,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: panDownY,
        rotation: 0,
      };
    }
    case "ken-burns": {
      const kbZoom = 1 + cycleTime * 0.3 * intensity;
      const kbX = cycleTime * 0.1 * intensity;
      const kbY = cycleTime * 0.05 * intensity;
      return {
        opacity: 1,
        scale: kbZoom,
        scaleX: 1,
        scaleY: 1,
        offsetX: kbX,
        offsetY: kbY,
        rotation: 0,
      };
    }
    case "none":
    default:
      return {
        opacity: 1,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: 0,
        rotation: 0,
      };
  }
}
